import { makeAutoObservable, runInAction } from "mobx";

import {
  acceptTeamInvitation,
  cancelTeamInvitation,
  getMemberTeams,
  getOwnTeam,
  getTeamRequests,
  leaveTeam,
  updateTeamUsers,
} from "../api/team";

import type {
  MemberTeam,
  OutgoingTeamRequest,
  OwnTeam,
  TeamRequest,
} from "../api/team";

class TeamStore {
  ownTeam: OwnTeam | null = null;
  memberTeams: MemberTeam[] = [];
  outgoingInvitations: OutgoingTeamRequest[] = [];
  incomingInvitations: TeamRequest[] = [];
  ownTeamLoading = false;
  memberTeamsLoading = false;
  outgoingInvitationsLoading = false;
  incomingInvitationsLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  async loadOwnTeam() {
    if (this.ownTeamLoading) return;
    this.ownTeamLoading = true;
    try {
      const team = await getOwnTeam();
      runInAction(() => {
        this.ownTeam = team;
      });
    } catch {
      // team not available (e.g. no premium)
    } finally {
      runInAction(() => {
        this.ownTeamLoading = false;
      });
    }
  }

  get isTeamMember() {
    return this.memberTeams.length > 0;
  }

  async loadMemberTeams() {
    if (this.memberTeamsLoading) return;
    this.memberTeamsLoading = true;
    try {
      const teams = await getMemberTeams();
      runInAction(() => {
        this.memberTeams = teams;
      });
    } catch {
      // silently handle
    } finally {
      runInAction(() => {
        this.memberTeamsLoading = false;
      });
    }
  }

  async updateMembers(users: { nextgis_guid: string }[]) {
    const updatedTeam = await updateTeamUsers(users);
    runInAction(() => {
      this.ownTeam = updatedTeam;
    });
    return updatedTeam;
  }

  async loadOutgoingInvitations() {
    if (this.outgoingInvitationsLoading) return;
    this.outgoingInvitationsLoading = true;
    try {
      const invitations = await getTeamRequests("out");
      runInAction(() => {
        this.outgoingInvitations = invitations;
      });
    } catch {
      // Keep the current list if pending invitations cannot be refreshed.
    } finally {
      runInAction(() => {
        this.outgoingInvitationsLoading = false;
      });
    }
  }

  async cancelOutgoingInvitation(guid: string) {
    await cancelTeamInvitation(guid);
    runInAction(() => {
      this.outgoingInvitations = this.outgoingInvitations.filter(
        (invitation) => invitation.guid !== guid,
      );
    });
  }

  async loadIncomingInvitations() {
    if (this.incomingInvitationsLoading) return;
    this.incomingInvitationsLoading = true;
    try {
      const invitations = await getTeamRequests("in");
      runInAction(() => {
        this.incomingInvitations = invitations;
      });
    } catch {
      // Keep the current list if pending invitations cannot be refreshed.
    } finally {
      runInAction(() => {
        this.incomingInvitationsLoading = false;
      });
    }
  }

  async acceptIncomingInvitation(guid: string) {
    await acceptTeamInvitation(guid);
    runInAction(() => {
      this.incomingInvitations = this.incomingInvitations.filter(
        (invitation) => invitation.guid !== guid,
      );
    });
    await this.loadMemberTeams();
  }

  async declineIncomingInvitation(guid: string) {
    await cancelTeamInvitation(guid);
    runInAction(() => {
      this.incomingInvitations = this.incomingInvitations.filter(
        (invitation) => invitation.guid !== guid,
      );
    });
  }

  async leaveMemberTeam(ownerGuid: string) {
    await leaveTeam(ownerGuid);
    runInAction(() => {
      this.memberTeams = this.memberTeams.filter(
        (team) => team.owner_id !== ownerGuid,
      );
    });
  }

  clear() {
    this.ownTeam = null;
    this.memberTeams = [];
    this.outgoingInvitations = [];
    this.incomingInvitations = [];
    this.ownTeamLoading = false;
    this.memberTeamsLoading = false;
    this.outgoingInvitationsLoading = false;
    this.incomingInvitationsLoading = false;
  }
}

export const teamStore = new TeamStore();
